import React, { useState } from "react";
import { Dialog } from "../containers/Dialog.tsx";
import { Button } from "../widgets/Button.tsx";
import { TextInput } from "../inputs/TextInput.tsx";
import { CreateFriendRequest, FriendRequest, User } from "../../api/types.ts";
import { useCreateFriendRequest } from "../../api/hooks.ts";
import { useDialog } from "../DialogManager.tsx";

interface FriendRequestDialogProps {
  open: boolean;
  closeDialog: () => void;
  profile?: User;
  friends: Partial<User>[];
  requests: FriendRequest[];
  outgoing: FriendRequest[];
}

const FriendRequestDialog: React.FC<FriendRequestDialogProps> = ({
  open,
  closeDialog,
  profile,
  friends,
  requests,
  outgoing,
}) => {
  const { setError } = useDialog();

  const createFriendRequestMutation = useCreateFriendRequest();

  const [email, setEmail] = useState<string>("");
  const [emailValidationError, setEmailValidationError] = useState<
    string | null
  >(null);

  const isValid = email.length > 3 && emailValidationError === null;

  const handleSave = () => {
    if (!profile) {
      return;
    }

    setEmailValidationError(null);

    if (profile.email === email) {
      setEmailValidationError(
        "Sending a friend request to yourself is not allowed",
      );
      return;
    }

    if (friends.some((friend) => friend.email === email)) {
      setEmailValidationError("You already have a friend with this email");
      return;
    }

    if (requests.some((req) => req.sender_email === email)) {
      setEmailValidationError(
        "You have a pending friend request from this email",
      );
      return;
    }

    if (outgoing.some((req) => req.recipient_email === email)) {
      setEmailValidationError(
        "You have a pending friend request to this email",
      );
      return;
    }

    const requestData: CreateFriendRequest = {
      recipient_email: email,
    };

    createFriendRequestMutation.mutate(requestData, {
      onSuccess: closeDialog,
      onError: (e) => setError(e),
    });
  };

  return (
    <Dialog open={open} onClose={closeDialog} title={"Send Friend Request"}>
      <Dialog.Content>
        <p>
          The recipient will receive a friend request if they are registered to
          seriv.io with the given email. This is reflected in the{" "}
          <span className="italic">Outgoing</span> section.
        </p>
        <TextInput
          label="Recipient Email"
          type="email"
          value={email || ""}
          onChange={(e) => {
            if (emailValidationError) {
              setEmailValidationError(null);
            }
            setEmail(e.target.value);
          }}
          errorText={emailValidationError ?? undefined}
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          variant="submit"
          onClick={handleSave}
          disabled={!isValid}
          loading={createFriendRequestMutation.isPending}
        >
          Send
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export { FriendRequestDialog };
